import Button from '@/presentation/components/elements/Button';
import Price from '@/presentation/components/elements/Price';
import ConnectButton from '@/presentation/components/modules/ConnectButton';
import {
  ArrowRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import Image from 'next/image';
import { useFormatter, useTranslations } from 'use-intl';
import { BaseError, formatEther } from 'viem';

const UnstakeForm = ({
  balance,
  unstake,
  isUnstakeConfirmed,
  isUnstakeConfirming,
  isUnstakePending,
  error,
  isConnected,
  lockedUntil,
  ethValue,
}: {
  balance?: bigint;
  unstake: () => void;
  isUnstakeConfirmed: boolean;
  isUnstakePending: boolean;
  isUnstakeConfirming: boolean;
  error: Error | null;
  isConnected: boolean;
  lockedUntil?: bigint;
  ethValue?: bigint;
}) => {
  const t = useTranslations('Stake.StakeForm');
  const { number } = useFormatter();

  return (
    <form className="space-y-4">
      <div className="rounded-xl bg-black py-4 px-5">
        <header className="text-xs text-gray-200 mb-4">
          <label className="">{'Amount'}</label>
        </header>
        <fieldset className="flex items-center">
          <Image
            src="/images/underscore.svg"
            height={60}
            width={60}
            alt="FLOOR"
            className="flex-none"
          />
          <span className="mx-4 font-semibold text-lg">{'FLOOR'}</span>
          <input
            type="text"
            className="w-full max-w-[16rem] min-w-0 rounded-xl border border-gray-400 bg-black text-white ml-auto font-semibold text-lg p-2 text-right focus:ring-2 focus:ring-gray-300 focus:outline-none"
            value={number(Number(formatEther(balance || 0n)), {
              maximumFractionDigits: 3,
              useGrouping: false,
            })}
            disabled={true}
          />
        </fieldset>
        <div className="text-right text-xs text-gray-200">
          {'~'}
          <Price
            value={ethValue}
            minimumFractionDigits={2}
            maximumFractionDigits={4}
          />
        </div>
      </div>
      {lockedUntil ? (
        <aside className="p-2 text-xs bg-brand-blue/10 rounded-md flex items-center">
          <InformationCircleIcon className="h-5 w-5 mr-2 flex-none" />
          <p>
            {'Due to '}
            <a
              href="https://forum.floor.xyz/t/fip77-project-new-wind/600"
              target="_blank"
              rel="nofollow noreferrer"
              className="text-brand-blue underline hover:no-underline"
            >
              {'FIP77'}
            </a>{' '}
            {' all tokens are now unlocked. '}
          </p>
        </aside>
      ) : null}
      <dl className="text-xs space-y-4">
        <div className="flex items-center w-full">
          <dt className="text-gray-200">{'Locked Until'}</dt>
          <dd className="flex-1 ml-4 text-right">{'Unlocked'}</dd>
        </div>
        <div className="flex items-center w-full">
          <dt className="text-gray-200">{'Staked'}</dt>
          <dd className="flex flex-1 ml-4 items-center justify-end space-x-1">
            <span className="relative">
              {number(Number(formatEther(balance || 0n)), {
                maximumFractionDigits: 3,
              })}
              {' FLOOR'}
              <span className="text-xxs absolute top-full text-gray-200 -mt-0.5 right-0">
                {'~'}
                <Price
                  value={ethValue}
                  minimumFractionDigits={2}
                  maximumFractionDigits={4}
                />
              </span>
            </span>
            <ArrowRightIcon className="text-brand-blue h-4 w-4" />
            <span className="relative">
              {'0 FLOOR'}
              <span className="text-xxs absolute top-full text-gray-200 -mt-0.5 right-0 whitespace-nowrap">
                {'~'}
                <Price value={0n} />
              </span>
            </span>
          </dd>
        </div>
        <div className="flex items-center w-full">
          <dt className="text-gray-200">{'Early exit penalty'}</dt>
          <dd className="flex-1 ml-4 text-right text-brand-red">
            {'0'}
            {' FLOOR'}
          </dd>
        </div>
        <div className="flex items-center w-full">
          <dt className="text-gray-200">{'You receive'}</dt>
          <dd className="flex flex-1 ml-4 items-center justify-end space-x-1">
            <span>
              {number(Number(formatEther(balance || 0n)))}
              {' FLOOR'}
            </span>
          </dd>
        </div>
      </dl>
      {isConnected ? (
        <Button
          className="w-full"
          onClick={unstake}
          processing={isUnstakeConfirming}
          disabled={isUnstakePending || balance === 0n}
        >
          {isUnstakeConfirmed ? 'Unstaked!' : 'Unstake'}
        </Button>
      ) : (
        <ConnectButton className="w-full" />
      )}
      {error &&
        !(error as BaseError)?.details?.includes(
          'User rejected the request',
        ) && (
          <div className="bg-red-500 text-white p-4 rounded-md text-xs">
            <h4 className="font-bold">{error?.name}</h4>
            <pre className="text-xs overflow-x-scroll">{error?.message}</pre>
          </div>
        )}
    </form>
  );
};

export default UnstakeForm;
